import React, { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  Container,
  Row,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Table,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import OnlyHeader from "components/Layout/OnlyHeader";
import TableComponent from "components/shared/Table";
import { fetchDriverRequests } from "store/actions/driverAction";
import { fetchOrderDetails } from "store/actions/driverAction";
import { changeRequestStatus } from "store/actions/driverAction";
import { orderPaymentRefund } from "store/actions/orderActions";
import { toast } from "react-toastify";
import AssignDriver from "components/Modals/AssignDriver";

const DriverRequests = () => {
  const dispatch = useDispatch();
  const {
    driverData,
    requestLoading,
    driverRequests,
    orderDetails,
    orderDetailsLoading,
  } = useSelector((state) => state.driver);
  const { uid } = useSelector((state) => state.auth);

  const [orderModal, setOrderModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [confirmActionType, setConfirmActionType] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [currentDeliveryDetails, setCurrentDeliveryDetails] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const assignToggle = () => {
    setOpenModal(!openModal);
  };

  const handleConfirmAction = async () => {
    if (!selectedRequest || !confirmActionType) return;

    if (confirmActionType === "accepted") {
      // Check for available
      const availableDrivers = driverData?.filter(
        (driver) => driver.isVerified && !driver.isAssigned
      );

      if (availableDrivers?.length > 0) {
        setSelectedOrderId(selectedRequest.orderId);
        setOpenModal(true);
        setConfirmModal(false);
        return;
      } else {
        // No drivers available, process refund
        setActionLoading(true);
        try {
          await dispatch(
            orderPaymentRefund(orderDetails, () => {
              dispatch(
                changeRequestStatus(
                  selectedRequest.id,
                  selectedRequest.driverId,
                  "completed"
                )
              );
            })
          );
          toast.info("No drivers available. Order has been refunded.");
        } catch (error) {
          console.error("Error processing refund:", error);
          toast.error(error.message || "Failed to process refund");
        } finally {
          setActionLoading(false);
          setConfirmModal(false);
        }
        return;
      }
    }

    // For rejections or if not handling acceptance
    setActionLoading(true);
    try {
      await dispatch(
        changeRequestStatus(
          selectedRequest.id,
          selectedRequest.driverId,
          confirmActionType
        )
      );
    } catch (error) {
      console.error("Error updating request status:", error);
      toast.error(error.message || "Failed to update request status");
    } finally {
      setActionLoading(false);
      setConfirmModal(false);
    }
  };

  const handleDriverAssignmentComplete = async () => {
    if (selectedRequest) {
      try {
        await dispatch(
          changeRequestStatus(
            selectedRequest.id,
            selectedRequest.driverId,
            "completed"
          )
        );
      } catch (error) {
        console.error("Error updating request status:", error);
        toast.error(error.message || "Failed to update request status");
      }
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp || !timestamp.seconds) return "-";
    const date = new Date(timestamp.seconds * 1000);
    const formttedDate = date.toLocaleDateString();
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${formttedDate} at ${formattedTime}`;
  };

  const handleOrderDetails = (item) => {
    setSelectedOrderId(item?.orderId);
    setCurrentDeliveryDetails({
      name: item?.driverInfo?.name,
      email: item?.driverInfo?.email,
      totalPrice: null,
    });
    setOrderModal(true);
  };

  useEffect(() => {
    if (!uid) return;
    const unsubscribe = dispatch(fetchDriverRequests(uid));
    return () => unsubscribe;
  }, [dispatch, uid]);

  useEffect(() => {
    if (orderDetails && orderModal) {
      setCurrentDeliveryDetails((prev) => ({
        ...prev,
        totalPrice: orderDetails.totalAmount,
      }));
    }
  }, [orderDetails, orderModal]);

  useEffect(() => {
    if (orderModal || selectedOrderId) {
      dispatch(fetchOrderDetails(selectedOrderId));
    }
  }, [orderModal, selectedOrderId, dispatch]);

  return (
    <>
      <OnlyHeader />
      <Container className="mt--7 mb-5" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="d-lg-flex  d-sm-block justify-content-between">
                <h3 className="pt-2 mr-2">Driver Requests</h3>
              </CardHeader>

              {requestLoading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <Spinner size="md" color="primary" />
                </div>
              ) : (
                <>
                  <TableComponent
                    className="align-items-center table-flush"
                    responsive
                  >
                    {driverRequests?.length === 0 ? (
                      <tbody>
                        <tr>
                          <td className="text-center py-5" colSpan="5">
                            No Driver Requests to display
                          </td>
                        </tr>
                      </tbody>
                    ) : (
                      <>
                        <thead className="thead-light">
                          <tr>
                            <th scope="col">Driver Name</th>
                            <th scope="col">Reason</th>
                            <th scope="col">Request Time</th>
                            <th scope="col">Status</th>
                            <th scope="col">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {driverRequests?.map((item, index) => (
                            <tr key={index} className="text-capitalize">
                              <td>{item?.driverInfo?.name}</td>
                              <td>{item?.reason}</td>
                              <td>{formatTimestamp(item?.createdAt)}</td>
                              <td>{item?.status}</td>
                              <td>
                                <Button
                                  className="btn-sm"
                                  color="info"
                                  onClick={() => handleOrderDetails(item)}
                                >
                                  Order Details
                                </Button>
                                <Button
                                  className="btn-sm"
                                  color="primary"
                                  onClick={() => {
                                    setSelectedRequest(item);
                                    setSelectedOrderId(item?.orderId);
                                    setConfirmActionType("accepted");
                                    setConfirmModal(true);
                                  }}
                                >
                                  Accept
                                </Button>

                                <Button
                                  className="btn-sm"
                                  color="danger"
                                  onClick={() => {
                                    setSelectedRequest(item);
                                    setSelectedOrderId(item?.orderId);
                                    setConfirmActionType("rejected");
                                    setConfirmModal(true);
                                  }}
                                >
                                  Reject
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </>
                    )}
                  </TableComponent>
                </>
              )}
            </Card>
          </div>
        </Row>
      </Container>

      {/* Order Details Modal */}
      <Modal
        isOpen={orderModal}
        toggle={() => setOrderModal(!orderModal)}
        size="lg"
      >
        <ModalHeader toggle={() => setOrderModal(!orderModal)}>
          <h3>Delivery Details</h3>

          <div className="mt-4">
            <h4 className="my-2">
              <b>Assigned Driver:</b> {currentDeliveryDetails?.name || "N/A"}
            </h4>
            <h4 className="my-2">
              <b>Driver Email:</b> {currentDeliveryDetails?.email || "N/A"}
            </h4>
          </div>
        </ModalHeader>
        <ModalBody>
          {orderDetailsLoading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner size="md" color="primary" />
            </div>
          ) : orderDetails ? (
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Quantity</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {orderDetails?.order?.map((item, index) => (
                  <tr key={index}>
                    <td>{item.name}</td>
                    <td>{item.quantity}</td>
                    <td>${item?.totalPrice || item?.price}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan="2" className="text-start font-weight-bold">
                    Total Price
                  </td>
                  <td className="font-weight-bold">
                    ${currentDeliveryDetails?.totalPrice}
                  </td>
                </tr>
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-3">No order details found.</div>
          )}
        </ModalBody>
      </Modal>
      <Modal isOpen={confirmModal} toggle={() => setConfirmModal(false)}>
        <ModalHeader toggle={() => setConfirmModal(false)}>
          {confirmActionType === "accepted"
            ? "Accept Request"
            : "Reject Request"}
        </ModalHeader>
        <ModalBody>
          <h5>
            Are you sure you want to{" "}
            {confirmActionType === "accepted" ? "Accept" : "Reject"} this
            request?
          </h5>
          <div className="d-flex justify-content-end mt-4">
            <Button
              color="danger"
              onClick={() => setConfirmModal(false)}
              className="mr-2"
            >
              Cancel
            </Button>
            <Button
              color={confirmActionType === "accepted" ? "primary" : "danger"}
              onClick={handleConfirmAction}
              disabled={actionLoading}
            >
              {actionLoading ? (
                <Spinner size="sm" />
              ) : confirmActionType === "accepted" ? (
                "Accept"
              ) : (
                "Reject"
              )}
            </Button>
          </div>
        </ModalBody>
      </Modal>
      <AssignDriver
        openModal={openModal}
        assignToggle={assignToggle}
        selectedOrderId={selectedOrderId}
        onAssignmentComplete={handleDriverAssignmentComplete}
      />
    </>
  );
};

export default DriverRequests;
